import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { CaseInsensitiveMap } from "Global/Base/DataTypes/CaseInsensitiveMap";
import { AsyncTree } from "../../../Global/Base/DataTypes/AsyncTree";
import { GlobalContext } from "../../../Global/Base/GlobalContext";
import { CancellationToken } from "../../../Global/Base/Promises/CancellationTokenSource";
import { EMPTY_ITEM_RIGHTS_COLLECTION, IItemRightsCollection } from "../../../Global/Base/Rights/RightTypes";
import { IBaseEditableControlRights } from "../../UI.BaseControls/ControlInterfaces";
import { ActionType, IAction, IMultiObjectAction } from "../../UI.Controls/Actions/IAction";
import { INodeAction } from "../../UI.Controls/Actions/INodeAction";
import { MenuType } from "../../UI.Controls/Actions/MenuType";
import { createElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { RightsEvaluationOverridesType } from "../Rights/Definitions";
import { loadOriginalRightsDefinition } from "../Rights/OriginalRightsDefinitionLoader";
import {
    IAttrNameControl,
    ICompositeComponent,
    IGenericControl,
    IHasFrameContext,
    IHTMLElementWithControl,
} from "../UI/Controls/ControlInterfaces";
import { ToolbarGroup } from "../UI/Toolbar/ToolbarGroup";
import { ToolbarGroups } from "../UI/Toolbar/ToolbarGroups";
import { FrameContext } from "../UI/UIManagerInterfaces";
import { IMultiActionToolbarButton } from "../UI/XGControls/Actions/IToolbarButton";
import { NodeAction } from "../UI/XGControls/Actions/NodeAction";
import { StandardAction } from "../UI/XGControls/Actions/StandardAction";
import { IFrame, IFrameWithRights } from "../UI/XGControls/IFrame";
import { getControlType } from "../Utilities/SharedUIMethods";
import { ContainerControlWrapper } from "./ContainerControlWrapper";
import { ControlWrapper } from "./ControlWrapper";
import { ComponentTreeType } from "./Definitions";
import { ILogicalComponent } from "./ILogicalComponent";

const logger = LazyLoadingSectionLogger.create(LoggingSection.formComponentsStructure);

export enum ComponentType {
    attributeComponent,
    methodComponent,
}
/**
 * Typ tovární funkce vytvářející speciální uzly stromu
 */
export type SpecialTreeNodeFactory = (controlWrapper: ILogicalComponent, parentNode: ComponentTreeType) => ComponentTree | undefined;

export class ComponentTree extends AsyncTree<ILogicalComponent> implements ComponentTreeType {
    private static _BEHAVIOUR_ATTRIBUTE_NAME = "data-behaviour";
    private static _CONTAINER_CONTROL_BEHAVIOR_NAMES = ["frame", "container"];

    protected static _excludedComponentTypes: Function[] = [ToolbarGroups, ToolbarGroup];
    /**
     * Vynechá při vytváření stromu tyto komponenty včetně jejich potomků
     */
    private static _excludedComponents: (() => Function)[] = ComponentTree._createExcludedComponents(ComponentTree._excludedComponentTypes);

    private _parentNode: ComponentTreeType | null;
    private static _registeredNodeFactories: CaseInsensitiveMap<SpecialTreeNodeFactory> = new CaseInsensitiveMap<SpecialTreeNodeFactory>(); //seznam továren speciálních uzlů
    protected _buildParameters: IBuildComponentTreeParameters;
    private _isPreinitializedOnly: boolean = false;

    constructor(component: ILogicalComponent, parentNode: ComponentTreeType) {
        super(component);

        if (parentNode)
            this._parentNode = parentNode;
    }

    public get component(): ILogicalComponent {
        return this.element;
    }

    public get parentNode(): ComponentTreeType | null {
        return this._parentNode;
    }

    public get attrName(): string {
        return this.element.attrName;
    }

    public get componentType(): ComponentType | null {
        return this.element.componentType;
    }

    public get condition(): string {
        return this.element.condition;
    }

    public get componentRightsCollection(): IItemRightsCollection {
        return this.component.rightsCollection;
    }

    public get isStatic(): boolean | null {
        return this.element.isStatic;
    }

    public get isPreinitializedOnly(): boolean {
        return this._isPreinitializedOnly;
    }

    public set isPreinitializedOnly(value: boolean) {
        this._isPreinitializedOnly = value;
    }

    public setChildComponents(childComponents: this[]): void {
        this.addNodeRange(childComponents);
    }

    /**
     * Sestaví kompletní strom komponent pro předaný frame
     */
    public static buildComponentTree(control: IGenericControl, buildParameters?: IBuildComponentTreeParameters): ComponentTree {
        const newTreeResult = ComponentTree.buildTscComponentTree(control, buildParameters);
        return newTreeResult.treeNode;
    }

    /**
     * Vytvoří pole typů tříd vynechaných při vytváření stromu
     * @param classTypes Pole tříd
     */
    private static _createExcludedComponents(classTypes: Function[]): (() => Function)[] {
        const excludedArray: (() => Function)[] = [];

        for (const classType of classTypes) {
            excludedArray.push(() => classType);
        }

        return excludedArray;
    }

    private static _createWrapperForControl(control: IGenericControl, rightsEvaluationOverrides?: RightsEvaluationOverridesType, parentNode?: ComponentTree): ILogicalComponent {
        const isContainerControl: boolean = ComponentTree._CONTAINER_CONTROL_BEHAVIOR_NAMES.includes(control.element.getAttribute(ComponentTree._BEHAVIOUR_ATTRIBUTE_NAME));
        const parentControlWrapper = parentNode && parentNode.component;
        const controlWrapper = isContainerControl ? new ContainerControlWrapper(control, parentControlWrapper) : new ControlWrapper(control, parentControlWrapper, rightsEvaluationOverrides && ComponentTree._hasRightsIgnored(control, rightsEvaluationOverrides));

        if (rightsEvaluationOverrides) {
            this._tryAddRightsOverrides(controlWrapper, rightsEvaluationOverrides);
        }

        return controlWrapper;
    }

    /**
     * Zaregistruje továrnu speciálních uzlů stromu.
     * @param type Třída, která reprezentuje nový uzel. Musí dědit z ComponentTree a mít statickou tovární metodu `createNodeFactory`.
     */
    public static registerSpecialNodeFactory(type: { new(component: ILogicalComponent, parentNode: ComponentTreeType): ComponentTree }, createNodeFactory: SpecialTreeNodeFactory): void {
        const key = type.name;
        if (!this._registeredNodeFactories.has(key))
            this._registeredNodeFactories.set(key, createNodeFactory);
    }

    /**
     * Funkce, která vytváří uzel stromu
     */
    protected static _createComponentTreeNode(controlWrapper: ILogicalComponent, parentNode: ComponentTreeType, _buildParameters?: IBuildComponentTreeParameters): ComponentTree {
        for (const factory of ComponentTree._registeredNodeFactories.values()) {
            const result = factory(controlWrapper, parentNode);
            if (result) {
                return result;
            }
        }
        return new ComponentTree(controlWrapper, parentNode);
    }

    public static buildTscComponentNode(control: IGenericControl, buildParameters: IBuildComponentTreeParameters, parentNode?: ComponentTree, ct?: CancellationToken): ComponentTree {
        let resultTree: ComponentTree;
        ct?.throwIfCancellationRequested();

        if (!(control instanceof IFrameWithRights) || !buildParameters || buildParameters && !buildParameters.isPreinitialized) {
            // Pokud už je framová komponenta před-inicializována, nevytváříme jí znovu wrapper a uzel stromu
            //const isContainerControl: boolean = ComponentTree._CONTAINER_CONTROL_BEHAVIOR_NAMES.includes(control.element.getAttribute(ComponentTree._BEHAVIOUR_ATTRIBUTE_NAME));
            //const parentControlWrapper = parentNode && parentNode.component;
            //const controlWrapper = isContainerControl ? new ContainerControlWrapper(control, parentControlWrapper) : new ControlWrapper(control, parentControlWrapper, ComponentTree._hasRightsIgnored(control, buildParameters.rightsEvaluationOverrides));
            const controlWrapper = ComponentTree._createWrapperForControl(control, buildParameters && buildParameters.rightsEvaluationOverrides, parentNode);
            //if (buildParameters && buildParameters.rightsEvaluationOverrides) {
            //    ////const rightsOverrides = rightsEvaluationOverrides.getOverridesForComponent(Object.getPrototypeOf(control).constructor);
            //    //const rightsOverrides = buildParameters.rightsEvaluationOverrides.getOverridesForComponent((<any>control).constructor); // Tohle by podle mě mělo normálně jít i bez <any>...
            //    //if (rightsOverrides)
            //    //    controlWrapper.rightsEvaluationOverrides = rightsOverrides;
            //    ComponentTree._tryAddRightsOverrides(controlWrapper, buildParameters.rightsEvaluationOverrides);
            //}

            resultTree = this._createComponentTreeNode(controlWrapper, parentNode);

            if (control instanceof IFrameWithRights && (!buildParameters || !buildParameters.skipComponentTreeAssignment)) {
                control.componentTree = resultTree;
            }
        } else {
            resultTree = control.componentTree as ComponentTree;
        }

        return resultTree;
    }

    /**
     * Sestaví strom tsc komponent pro předaný frame
     */
    public static buildTscComponentTree<T extends ComponentTree>(control: IGenericControl, buildParameters: IBuildComponentTreeParameters, parentNode?: ComponentTree, ct?: CancellationToken): IAsyncComponentTreeBuildResult<T> {
        const resultTree = this.buildTscComponentNode(control, buildParameters, parentNode, ct);
        if ((control instanceof IHasFrameContext) && !(control instanceof IFrame)) {
            control.frameContext = buildParameters.frameContext;
        }
        if (control instanceof IFrameWithRights) {
            resultTree._buildParameters = buildParameters;
            if (buildParameters) {
                if (buildParameters.stopTreeBuildOnNextFrame) {
                    resultTree.isPreinitializedOnly = true; // Pokud nebudeme pokračovat na akce a potomky, pak je uzel pouze před-inicializovaný
                    return { treeNode: resultTree } as IAsyncComponentTreeBuildResult<T>;
                }
            } else
                buildParameters = {};
            buildParameters.stopTreeBuildOnNextFrame = true;
        }

        const children = ComponentTree._findTscControlChildren(control.element);

        let foundActions: ComponentTree[] = [];
        if (control instanceof IAction)
            foundActions.push(resultTree);

        let processedChildren: T[] = [];
        for (const child of children) {
            const buildChildParams: IBuildComponentTreeParameters = {
                frameContext: buildParameters?.frameContext,
                rightsEvaluationOverrides: buildParameters?.rightsEvaluationOverrides,
                stopTreeBuildOnNextFrame: buildParameters && buildParameters.stopTreeBuildOnNextFrame,
            };
            if (child instanceof IFrameWithRights)
                buildChildParams.isPreinitialized = !!child.componentTree;
            const componentTreeSubresult = ComponentTree.buildTscComponentTree<T>(child, buildChildParams, resultTree, ct);
            const treeNode = componentTreeSubresult.treeNode;
            if (!(child instanceof IAction)) {
                processedChildren = processedChildren.concat(treeNode);
            }
            if (componentTreeSubresult.actions && componentTreeSubresult.actions.length > 0)
                foundActions = foundActions.concat(componentTreeSubresult.actions);
        }

        if (processedChildren.length > 0)
            resultTree.setChildComponents(processedChildren);

        if (control instanceof IFrameWithRights) {
            let actionsSubtree: ComponentTree;
            if (foundActions.length > 0) {
                actionsSubtree = ComponentTree._buildActionsTree(foundActions, resultTree);
                foundActions = [];
            }

            if (control.menu) {
                const frameMenuActionsSubtree = resultTree._buildFrameMenuStandardActionsSubtree(control, buildParameters, resultTree);
                if (frameMenuActionsSubtree) {
                    if (actionsSubtree)
                        actionsSubtree.addNode(frameMenuActionsSubtree);
                    else
                        actionsSubtree = frameMenuActionsSubtree;
                }
            }

            if (actionsSubtree)
                resultTree.addNode(actionsSubtree);
        }

        return { treeNode: resultTree, actions: foundActions } as IAsyncComponentTreeBuildResult<T>;
    }

    /**
     * V podstromu předaného uzlu stromu se pokusí najít uzel s akcemi a do něj do první úrovně přidat akce
     * @param componentTree Uzel stromu ve kterém se má hledat podstrom akcí
     * @param actions Akce, které se mají přidat do podstromu akcí
     * @param rightsEvaluationOverrides Overrides
     * @returns Vstupní uzel stromu, pokud byly akce přidány. Jinak null
     */
    public static addActionsIntoActionsSubTree(componentTree: ComponentTree, rightsEvaluationOverrides: RightsEvaluationOverridesType, actions: IAction[]): ComponentTree | null {
        const virtualRootNodeAction = ComponentTree._findNodeDFS(componentTree, (treeNode) => treeNode.component.isVirtualControl && treeNode.component.control instanceof INodeAction);

        if (virtualRootNodeAction) { // TODO-rites: X pokud neexistuje vytvořit?; c2: asi ne, není kde....! co kdyby tam byl předán nějakej nesmyslnej node..tak to do něj přece přidávat nebudu
            for (const action of actions) {
                if (!(action instanceof IGenericControl))
                    continue;

                const actionWrapper = ComponentTree._createWrapperForControl(action, rightsEvaluationOverrides, virtualRootNodeAction);
                const actionNode = new ComponentTree(actionWrapper, virtualRootNodeAction);

                virtualRootNodeAction.addNode(actionNode);
            }

            return componentTree;
        }

        return null;
    }

    /**
     * Najde všechny nejbližší podřízené tsc elementy nehledě na obyčejné HTML elementy(např. div)
     */
    private static _findTscControlChildren(element: HTMLElement): IGenericControl[] {
        const isExcluded = (control: IGenericControl): boolean => {
            for (const type of ComponentTree._excludedComponents) {
                if (control instanceof type())
                    return true;
            }
            return false;
        };

        let foundChildren: IGenericControl[] = [];

        //Nechceme aby kompozitní komponenty umožnili vyhodnocovat práva svým potomkům. Např. DateTimeEdit se skládá z DateEdit a TimeEdit.
        //Je na zodpovědnosti kompozitní komponenty, aby svá vyhodnocená práva aplikovala své potomky
        const component = element instanceof IHTMLElementWithControl ? element.tscControl : element;
        if (component instanceof ICompositeComponent && component.isCompositeComponent) {
            return [];
        }

        for (const child of element.childNodes) {
            if (child.nodeType === 1) { // 1 === ELEMENT_NODE
                if (child instanceof IHTMLElementWithControl) {
                    if (isExcluded(child.tscControl))
                        continue;
                    foundChildren.push(child.tscControl);
                } else if (child instanceof IBaseEditableControlRights && child instanceof HTMLElement) {
                    //Některé komponenty dědí z UI/UI/Control místo BaseControls/Control, a child je přímo komponenta, nad kterou chceme vyhodnocovat práva
                    //Zabalí se proto do adaptéru, aby se s nimi dalo pracovat jako s IGenericControl, který se používá ve stromu práv.

                    // Komponenta nemusí být nad atributem, proto se takto rozliší
                    const wrapperConstructor = child instanceof IAttrNameControl ? AttrBaseControlWrapper : BaseControlWrapper;
                    foundChildren.push(new wrapperConstructor(child));
                } else {
                    foundChildren = foundChildren.concat(ComponentTree._findTscControlChildren(child as HTMLElement));
                }
            }
        }

        return foundChildren;
    }

    private static _tryAddRightsOverrides(controlWrapper: ILogicalComponent, rightsEvaluationOverrides: RightsEvaluationOverridesType): void {
        const rightsOverrides = rightsEvaluationOverrides.getOverridesForComponent(/*ComponentTree._getType(*/controlWrapper/*.control)*/);
        if (rightsOverrides)
            controlWrapper.rightsEvaluationOverrides = rightsOverrides;
    }

    private static _hasRightsIgnored(control: IGenericControl, rightsEvaluationOverrides: RightsEvaluationOverridesType): boolean {
        return rightsEvaluationOverrides.isIgnored(getControlType(control));
    }

    /**
     * Funkce, která při sestavování stromu vyselektuje akce v menu framu, které mají být přidány a které přeskočeny.
     */
    protected _frameMenuActionsSelector(menuAction: IAction, frame: IFrameWithRights): boolean {
        if (menuAction.actionType === ActionType.node && menuAction.toMenu === MenuType.mtPrint)
            return true;

        //Do stromu se přidávají jen akce, které vytvořil aktuální frame
        return menuAction instanceof StandardAction && menuAction.owner === frame;
    }

    protected _buildFrameMenuStandardActionsSubtree(frame: IFrameWithRights, buildParams: IBuildComponentTreeParameters, parentNode?: ComponentTree /*, createdActionsCount?: IOutputNumericParameter*/): ComponentTree | null {
        const rightsEvaluationOverrides = buildParams && buildParams.rightsEvaluationOverrides ? buildParams.rightsEvaluationOverrides : undefined;
        const frameMenuActionsSelector = buildParams && buildParams.frameMenuActionsSelector ? buildParams.frameMenuActionsSelector : this._frameMenuActionsSelector;
        const treeNodes: ComponentTree[] = [];

        const menuActions = frame.menu.getActions();
        for (const menuAction of menuActions) {
            if (!(menuAction instanceof IGenericControl) || !frameMenuActionsSelector(menuAction, frame))
                continue;
            const parentControlWrapper = parentNode && parentNode.component;
            const menuActionWrapper = new ControlWrapper(menuAction, parentControlWrapper, rightsEvaluationOverrides && ComponentTree._hasRightsIgnored(menuAction, rightsEvaluationOverrides));
            if (rightsEvaluationOverrides)
                ComponentTree._tryAddRightsOverrides(menuActionWrapper, rightsEvaluationOverrides);
            const newTreeNode = new ComponentTree(menuActionWrapper, parentNode);
            treeNodes.push(newTreeNode);

            // Když se jedná o MultiObjectAction, tak do stromu přidáme i její ToolbarButton, kvůli správnému vyhodnocení přes overrides
            if (menuAction instanceof IMultiObjectAction) {
                if (menuAction?.toolbarButton instanceof IMultiActionToolbarButton) {
                    const toolbarButtonWrapper = new ControlWrapper(menuAction.toolbarButton, parentControlWrapper, rightsEvaluationOverrides && ComponentTree._hasRightsIgnored(menuAction.toolbarButton, rightsEvaluationOverrides));
                    if (rightsEvaluationOverrides)
                        ComponentTree._tryAddRightsOverrides(toolbarButtonWrapper, rightsEvaluationOverrides);
                    const newTreeNode = new ComponentTree(toolbarButtonWrapper, parentNode);
                    treeNodes.push(newTreeNode);
                } else {
                    logger.warn("For action of type IMultiObjectAction, toolbarButton must implement IMultiActionToolbarButton otherwise Rights will not be applied! ");
                }
            }
        }

        return ComponentTree._buildActionsTree(treeNodes, parentNode);
    }

    /**
     * Z před-vytvořených elementárních uzlů stromu obsahujících akce vytvoří strom hierarchicky odpovídající struktuře dané pomocí masterNode
     * @param actionNodes Pole uzlů stromu s akcemi
     */
    private static _buildActionsTree(actionNodes: ComponentTree[], parentNode?: ComponentTree/*, rightsEvaluationOverrides?: IRightsEvaluationOverrides*/): ComponentTree | null {
        if (!actionNodes || actionNodes.length <= 0)
            return null;

        const actionsOfMasterNodesMap: Map<string, Set<ComponentTree>> = new Map<string, Set<ComponentTree>>();
        const masterNodesMap: Map<string, ComponentTree> = new Map<string, ComponentTree>();
        const standaloneActions: Set<ComponentTree> = new Set<ComponentTree>();

        for (const actionNode of actionNodes) {
            if (actionNode.component && actionNode.component.control) {
                const action = actionNode.component.control;
                let isNodeAction = false;
                if (action instanceof INodeAction) {
                    masterNodesMap.set(action.ctrlID, actionNode);
                    isNodeAction = true;
                }

                if (action instanceof IAction) {
                    if (action.masterNode) {
                        let masterNodeItemsSet: Set<ComponentTree>;
                        if (!actionsOfMasterNodesMap.has(action.masterNode)) {
                            masterNodeItemsSet = new Set<ComponentTree>();
                            actionsOfMasterNodesMap.set(action.masterNode, masterNodeItemsSet);
                        } else {
                            masterNodeItemsSet = actionsOfMasterNodesMap.get(action.masterNode);
                        }

                        masterNodeItemsSet.add(actionNode);
                    } else if (!isNodeAction) { // NodeActiony bez masterNodu se zpracují z mapu masterNodesMap, nechceme je přidávat mezi samostatné(mimo NodeActiony) akce
                        standaloneActions.add(actionNode);
                    }
                } else if (action instanceof IMultiActionToolbarButton) {
                    standaloneActions.add(actionNode);
                }
            }
        }

        const rootNodeActionElement = createElement();
        const rootNodeAction = new NodeAction({ element: rootNodeActionElement, originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });
        const parentControlWrapper = parentNode && parentNode.component;
        const rootActionTreeNode = new ComponentTree(new ControlWrapper(rootNodeAction, parentControlWrapper, false, true), parentNode);

        for (const [masterNodeName, masterNodeItemsSet] of actionsOfMasterNodesMap) {
            let masterNodeTreeNode: ComponentTree;
            if (masterNodesMap.has(masterNodeName)) {
                masterNodeTreeNode = masterNodesMap.get(masterNodeName);
            } else if (masterNodeItemsSet && masterNodeItemsSet.size > 0) {
                // TODO: #vytvořit virtuální nodeaction pro toolbarové kategorie
                const missingNodeActionElement = createElement();
                const missingNodeAction = new NodeAction({ element: missingNodeActionElement, originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });
                missingNodeAction.ctrlID = masterNodeName; // TODO?
                masterNodeTreeNode = new ComponentTree(new ControlWrapper(missingNodeAction, parentControlWrapper), parentNode);
                masterNodesMap.set(masterNodeName, masterNodeTreeNode);
            }

            //const masterNodeItemsSet = actionsOfMasterNodesMap.get(masterNodeName);
            if (masterNodeItemsSet && masterNodeItemsSet.size > 0) {
                masterNodeTreeNode.addNodeRange(Array.from(masterNodeItemsSet));
            }

            if (masterNodeTreeNode.component.control instanceof INodeAction && !masterNodeTreeNode.component.control.masterNode)
                rootActionTreeNode.addNode(masterNodeTreeNode);
        }
        rootActionTreeNode.addNodeRange(Array.from(standaloneActions));

        return rootActionTreeNode;
    }

    public override replaceNode(nodeToAdd: this): this {
        if (this.parentNode) {
            this.parentNode.removeChildNode(this);
            this.parentNode.addNode(nodeToAdd);
            return nodeToAdd;
        }
        return nodeToAdd;
    }
}

GlobalContext.register("ComponentTree", ComponentTree); // TODO-rites: nechat?

export interface IBuildComponentTreeParameters {
    rightsEvaluationOverrides?: RightsEvaluationOverridesType;
    stopTreeBuildOnNextFrame?: boolean;
    isPreinitialized?: boolean;
    /** Funkce, která při sestavování stromu vyselektuje akce v menu framu, která mají být přidány a které přeskočeny.*/
    frameMenuActionsSelector?: (action: IAction, frame: IFrameWithRights) => boolean;
    /** Pokud je nastaveno, nebude na komponentě uložen nový strom komponent. Používá se při opakovaném sestavení stromu pro nalezení dynamických potomků. */
    skipComponentTreeAssignment?: boolean;
    /** Kontext framu */
    frameContext?: FrameContext;
}

export interface IAsyncComponentTreeBuildResult<T> /*extends IComponentTreeBuildResult<ILogicalComponent>*/ {
    treeNode: T;
    actions?: T[];
}
/**
 * Třída, která zabalí komponenty které dědí z TescoSW\UI\UI.BaseControls\EditableControl
 * a zároveň podporují nastavení práv
 */
class BaseControlWrapper implements IGenericControl, IBaseEditableControlRights {
    /** Viz {@link IWithOriginalRightsDefinition.originalRightsDefinition}. */
    private _originalRightsDefinition: IItemRightsCollection;

    constructor(public element: IBaseEditableControlRights & HTMLElement) {
        this._originalRightsDefinition = loadOriginalRightsDefinition(element);
    }

    public get enabled(): boolean {
        return this.element.enabled;
    }
    public set enabled(val: boolean) {
        this.element.enabled = val;
    }
    public get required(): boolean {
        return this.element.required;
    }
    public set required(val: boolean) {
        this.element.required = val;
    }
    public get visible(): boolean {
        return this.element.visible;
    }
    public set visible(val: boolean) {
        this.element.visible = val;
    }
    public get originalRightsDefinition(): IItemRightsCollection {
        return this._originalRightsDefinition;
    }
    public set originalRightsDefinition(_) {
        throw new Error("originalRightsDefinition setter unsupported");
    }

    public dispose(): void {
        // Prázdná metoda
    }
}

/**
 * @inheritdoc
 * A zároveň je nad nějakým atributem
 */
class AttrBaseControlWrapper extends BaseControlWrapper implements IAttrNameControl {
    public get attrName(): string {
        return this.element instanceof IAttrNameControl ? this.element.attrName : undefined;
    }
}
